import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { customInstance } from "@repo/api-client";

interface EntitlementsResponse {
  flags: Record<string, boolean>;
}

const fetchEntitlements = (venueId: string, signal?: AbortSignal): Promise<EntitlementsResponse> =>
  customInstance<EntitlementsResponse>(
    `/api/v1/venues/${encodeURIComponent(venueId)}/entitlements`,
    { method: "GET", signal },
  );

/**
 * The venue's derived feature flags (§C.3), loaded once at shell start. Shares the
 * `["venue", venueId, "entitlements"]` query key with the floor-plan feature, so the cache is one and
 * realtime resource-invalidation covers both.
 */
export function useVenueEntitlementsQuery(venueId: string): UseQueryResult<EntitlementsResponse> {
  return useQuery({
    queryKey: ["venue", venueId, "entitlements"],
    queryFn: ({ signal }) => fetchEntitlements(venueId, signal),
    staleTime: 60_000,
    enabled: venueId.length > 0,
  });
}

export interface VenueEntitlements {
  /**
   * Whether a feature is enabled for the current venue. Fail-CLOSED once the flags are known (an absent
   * or false key ⇒ hidden). Fail-OPEN when the read did not resolve (`flags` undefined): a transient
   * read error must NOT hide features — the server still enforces every gated action fail-closed, so the
   * worst case is a locked action showing an upgrade message rather than a feature vanishing on a glitch.
   */
  has: (key: string) => boolean;
  flags: Record<string, boolean>;
}

const Ctx = createContext<VenueEntitlements | null>(null);

export function EntitlementsProvider({
  flags,
  children,
}: {
  flags: Record<string, boolean> | undefined;
  children: ReactNode;
}) {
  const value = useMemo<VenueEntitlements>(
    () => ({
      flags: flags ?? {},
      has: (key) => (flags === undefined ? true : flags[key] === true),
    }),
    [flags],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The current venue's entitlements. Throws outside a `/v/:venueId` shell (a programming error). */
export function useVenueEntitlements(): VenueEntitlements {
  const value = useContext(Ctx);
  if (!value) throw new Error("useVenueEntitlements() must be used inside a VenueShellLayout");
  return value;
}
